// src/store/cartSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the structure of a CartItem
export interface CartItem {
  id: string;
  name: string;
  type: string;
  images?: string[];
  isActive: boolean;
  isSaleActive: boolean;
  selectedVariant: Variant;
}

export interface Variant {
  id: string;                 // Unique ID for the variant
  color: string;              // Color of the variant (if applicable)
  colorCode: string;
  design: string;
  imageUrl: string;           // Image URL for the variant (if different from the base image)
  material: string;           // Material of the variant (e.g., cotton, wool)
  description: string;
  size: string;               // Size of the variant (if applicable)
  unitPrice: number;              // Price of the variant
  salePrice: number;
  availableQuantity: number;  // Available stock for the variant
  quantity: number;           // Quantity of the variant added to the cart
 
}

// Define the structure of the cart state
interface CartState {
  items: CartItem[];
  isAnonymous: boolean; // Track if the user is anonymous
  totalItems: number; // Total number of items in the cart
}

// Initial state of the cart
const initialState: CartState = {
  items: [],
  isAnonymous: true, // Assume user starts as anonymous
  totalItems: 0, // Start with 0 total items
};

// Create the cart slice
const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // Add item to cart
    addItem: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(item => item.id === action.payload.id && item.selectedVariant.id === action.payload.selectedVariant.id);
      if (existingItem) {
        // Increment the quantity if the item already exists
        existingItem.selectedVariant.quantity += 1;
      } else {
        // Otherwise, add the new item to the cart
        state.items.push(action.payload);
      }
      // Recalculate totalItems based on quantities in the cart
      state.totalItems = state.items.reduce((total, currentItem) => total + currentItem.selectedVariant.quantity, 0);
    },

    // Remove item from cart by ID (and variant)
    removeItem: (state, action: PayloadAction<{ itemId: string, variantId: string }>) => {
      state.items = state.items.filter(item => !(item.id === action.payload.itemId && item.selectedVariant.id === action.payload.variantId));
      // Recalculate totalItems after removal
      state.totalItems = state.items.reduce((total, currentItem) => total + currentItem.selectedVariant.quantity, 0);
    },

    // Decrease the quantity of an item by ID (and variant)
    decreaseQuantity: (state, action: PayloadAction<{ itemId: string, variantId: string }>) => {
      const item = state.items.find(item => item.id === action.payload.itemId && item.selectedVariant.id === action.payload.variantId);
      if (item) {
        if (item.selectedVariant.quantity > 1) {
          // If quantity is greater than 1, decrease it
          item.selectedVariant.quantity -= 1;
        } else {
          // If quantity is 1 or less, remove the item
          state.items = state.items.filter(item => !(item.id === action.payload.itemId && item.selectedVariant.id === action.payload.variantId));
        }
        // Recalculate totalItems after quantity change
        state.totalItems = state.items.reduce((total, currentItem) => total + currentItem.selectedVariant.quantity, 0);
      }
    },

    // Clear the entire cart
    clearCart: (state) => {
      state.items = []; // Clear all items
      state.totalItems = 0; // Reset totalItems to 0
    },

    // Toggle anonymous status (e.g., when user logs in or logs out)
    toggleAnonymousStatus: (state) => {
      state.isAnonymous = !state.isAnonymous; // Toggle the anonymous flag
    },
  },
});

// Export the actions for use in components
export const { addItem, removeItem, decreaseQuantity, clearCart, toggleAnonymousStatus } = cartSlice.actions;

// Export the reducer to be included in the store
export default cartSlice.reducer;
